import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useNavigate } from 'react-router-dom';
import { ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons';
import { InlineMath } from 'react-katex';

export function CalorieEstimationExplanationPage() {
  const navigate = useNavigate();
  const [openSection, setOpenSection] = useState<string | null>(null);

  const toggleSection = (section: string) => {
    setOpenSection(openSection === section ? null : section);
  };

  return (
    <div className="min-h-screen bg-gradient-to-r from-blue-400 to-purple-500 py-12 px-4 sm:px-6 lg:px-8">
      <Card className="max-w-3xl mx-auto">
        <CardHeader>
          <CardTitle className="text-3xl font-bold">
            🧮 Understanding Your Calorie Estimate
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <section className="bg-blue-100 p-4 rounded-lg">
            <h2 className="text-2xl font-semibold mb-3">🚀 Quick Overview</h2>
            <p className="mb-4">
              Your personalized calorie estimate is based on three key factors:
            </p>
            <div className="space-y-4">
              <div className="flex items-center bg-white p-3 rounded-lg shadow-sm">
                <div className="text-4xl mr-4">🔥</div>
                <div>
                  <h3 className="font-semibold">Basal Metabolism</h3>
                  <p className="text-sm">Energy your body needs at rest</p>
                </div>
              </div>
              <div className="flex items-center bg-white p-3 rounded-lg shadow-sm">
                <div className="text-4xl mr-4">🏃‍♂️</div>
                <div>
                  <h3 className="font-semibold">Daily Activity</h3>
                  <p className="text-sm">Extra energy for your lifestyle</p>
                </div>
              </div>
              <div className="flex items-center bg-white p-3 rounded-lg shadow-sm">
                <div className="text-4xl mr-4">🎯</div>
                <div>
                  <h3 className="font-semibold">Your Goal</h3>
                  <p className="text-sm">Adjustments for weight goals</p>
                </div>
              </div>
            </div>
            <p className="mt-4 text-sm italic">
              This estimate provides a scientifically-backed starting point for your nutrition
              journey.
            </p>
          </section>

          <Collapsible>
            <CollapsibleTrigger
              onClick={() => toggleSection('calculation')}
              className="flex justify-between items-center w-full p-4 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <h2 className="text-2xl font-semibold">🧮 Calculation Process</h2>
              {openSection === 'calculation' ? (
                <ChevronUpIcon className="h-6 w-6" />
              ) : (
                <ChevronDownIcon className="h-6 w-6" />
              )}
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2 p-4 bg-gray-50 rounded-lg">
              <ol className="list-decimal list-inside space-y-2">
                <li>Calculate Basal Metabolic Rate (BMR) using the Mifflin-St Jeor Equation</li>
                <li>
                  Multiply BMR by an activity factor to get Total Daily Energy Expenditure (TDEE)
                </li>
                <li>Adjust TDEE based on your weight goal (lose, maintain, or gain)</li>
              </ol>
              <p className="mt-2">
                This process provides a scientifically-backed starting point for your calorie needs.
              </p>
            </CollapsibleContent>
          </Collapsible>

          <Collapsible>
            <CollapsibleTrigger
              onClick={() => toggleSection('details')}
              className="flex justify-between items-center w-full p-3 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <h2 className="text-xl font-semibold flex items-center">
                <span className="mr-2">🔬</span>
                Detailed Explanation
              </h2>
              {openSection === 'details' ? (
                <ChevronUpIcon className="h-5 w-5" />
              ) : (
                <ChevronDownIcon className="h-5 w-5" />
              )}
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2 p-4 bg-gray-50 rounded-lg space-y-6">
              <div>
                <h3 className="text-xl font-semibold mb-2">1. Basal Metabolic Rate (BMR)</h3>
                <p className="mb-2">
                  BMR is the energy your body needs at rest. We use the Mifflin-St Jeor Equation:
                </p>
                <div className="space-y-2">
                  <p>
                    Men:{' '}
                    <InlineMath math="BMR = 10 \times weight(kg) + 6.25 \times height(cm) - 5 \times age(y) + 5" />
                  </p>
                  <p>
                    Women:{' '}
                    <InlineMath math="BMR = 10 \times weight(kg) + 6.25 \times height(cm) - 5 \times age(y) - 161" />
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-2">
                  2. Total Daily Energy Expenditure (TDEE)
                </h3>
                <p className="mb-2">TDEE accounts for your activity level:</p>
                <div className="space-y-1">
                  <p>
                    Sedentary: <InlineMath math="TDEE = BMR \times 1.2" />
                  </p>
                  <p>
                    Lightly active: <InlineMath math="TDEE = BMR \times 1.375" />
                  </p>
                  <p>
                    Moderately active: <InlineMath math="TDEE = BMR \times 1.55" />
                  </p>
                  <p>
                    Very active: <InlineMath math="TDEE = BMR \times 1.725" />
                  </p>
                  <p>
                    Extra active: <InlineMath math="TDEE = BMR \times 1.9" />
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-2">3. Goal Adjustment</h3>
                <p className="mb-2">We adjust your TDEE based on your goal:</p>
                <div className="space-y-1">
                  <p>
                    Weight loss: Reduce by 10-20%{' '}
                    <InlineMath math="(TDEE \times 0.8 \text{ to } 0.9)" />
                  </p>
                  <p>Maintenance: Keep TDEE as is</p>
                  <p>
                    Weight gain: Increase by 10-20%{' '}
                    <InlineMath math="(TDEE \times 1.1 \text{ to } 1.2)" />
                  </p>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>

          <Collapsible>
            <CollapsibleTrigger
              onClick={() => toggleSection('factors')}
              className="flex justify-between items-center w-full p-4 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <h2 className="text-2xl font-semibold">🧬 Influencing Factors</h2>
              {openSection === 'factors' ? (
                <ChevronUpIcon className="h-6 w-6" />
              ) : (
                <ChevronDownIcon className="h-6 w-6" />
              )}
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2 p-4 bg-gray-50 rounded-lg">
              <p className="mb-2">Several factors influence your calorie needs:</p>
              <ul className="list-disc list-inside space-y-2">
                <li>Age: Metabolism typically slows with age</li>
                <li>Gender: Biological differences affect calorie requirements</li>
                <li>Height and Weight: Larger bodies generally need more energy</li>
                <li>Body Composition: Muscle mass increases calorie needs</li>
                <li>Activity Level: More movement requires more energy</li>
                <li>Health Conditions: Some conditions can affect metabolism</li>
                <li>Genetics: Individual variations can impact calorie needs</li>
              </ul>
            </CollapsibleContent>
          </Collapsible>

          <section className="bg-green-100 p-3 rounded-lg">
            <h2 className="text-xl font-semibold mb-2 flex items-center">
              <span className="text-2xl mr-2">💡</span>
              Helpful Tips
            </h2>
            <ul className="space-y-3">
              <li className="flex items-start">
                <span className="text-green-600 mr-2">•</span>
                <span>Your estimate is a great starting point for your journey</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 mr-2">•</span>
                <span>Feel free to adjust your intake based on how you feel and progress</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 mr-2">•</span>
                <span>Remember, the quality of your food is just as important as the quantity</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 mr-2">•</span>
                <span>For personalized advice, consider consulting a nutrition professional</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 mr-2">•</span>
                <span>Small, consistent changes often lead to the best long-term results</span>
              </li>
            </ul>
          </section>

          <Button onClick={() => navigate('/profile')} className="mt-4 w-full">
            ← Back to Profile
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
